import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../../hooks/ApiProvider/ApiProvider';
import { useMembership } from '../../../hooks/MembershipProvider/MembershipProvider';
import styles from './CouponEdit.module.css';

interface CouponFormData {
  code: string;
  name: string;
  description: string;
  discount_type: string;
  discount_value: number;
  min_order_amount: number;
  max_discount_amount: number;
  usage_limit: number;
  coupon_type: string;
  valid_from: string;
  valid_until: string;
  status: string;
}

interface CouponInfo {
  id: string;
  code: string;
  name: string;
  description?: string;
  discount_type: string;
  discount_value: number;
  min_order_amount?: number;
  max_discount_amount?: number;
  usage_limit?: number;
  used_count: number;
  status: string;
  coupon_type: string;
  valid_from: string;
  valid_until: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

const CouponEdit: React.FC = () => {
  const { couponId } = useParams<{ couponId: string }>();
  const { getCoupon, createCoupon, updateCoupon } = useApi();
  const { hasAdminPermission, hasSystemPermission } = useMembership();
  const navigate = useNavigate();
  
  const isEdit = !!couponId;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coupon, setCoupon] = useState<CouponInfo | null>(null);

  const [formData, setFormData] = useState<CouponFormData>({
    code: '',
    name: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 0,
    min_order_amount: 0,
    max_discount_amount: 0,
    usage_limit: 0,
    coupon_type: 'discount',
    valid_from: '',
    valid_until: '',
    status: 'active'
  });

  // 폼 데이터 초기화
  const initializeFormData = (couponData?: CouponInfo) => {
    if (couponData) {
      setFormData({
        code: couponData.code,
        name: couponData.name,
        description: couponData.description || '',
        discount_type: couponData.discount_type,
        discount_value: couponData.discount_value,
        min_order_amount: couponData.min_order_amount || 0,
        max_discount_amount: couponData.max_discount_amount || 0,
        usage_limit: couponData.usage_limit || 0,
        coupon_type: couponData.coupon_type,
        valid_from: couponData.valid_from.split('T')[0],
        valid_until: couponData.valid_until.split('T')[0],
        status: couponData.status
      });
    } else {
      // 새 쿠폰 생성 시 기본값 설정
      const today = new Date();
      const nextMonth = new Date(today);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      
      setFormData({
        code: '',
        name: '',
        description: '',
        discount_type: 'percentage',
        discount_value: 10,
        min_order_amount: 0,
        max_discount_amount: 0,
        usage_limit: 0,
        coupon_type: 'discount',
        valid_from: today.toISOString().split('T')[0],
        valid_until: nextMonth.toISOString().split('T')[0],
        status: 'active'
      });
    }
  };

  // 쿠폰 상세 정보 조회 (수정 모드일 때)
  const fetchCouponDetail = async () => {
    if (!couponId || !isEdit) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await getCoupon(couponId);
      setCoupon(response);
      initializeFormData(response);
    } catch (err) {
      console.error('쿠폰 상세 조회 실패:', err);
      setError(err instanceof Error ? err.message : '쿠폰 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchCouponDetail();
    } else {
      initializeFormData();
    }
  }, [couponId, isEdit]);

  // 폼 입력 핸들러
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('_amount') || name.includes('_value') || name.includes('usage_limit') 
        ? (value === '' ? 0 : Number(value))
        : value
    }));
  };

  // 폼 제출 핸들러
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      // 폼 검증
      if (!formData.code.trim()) {
        throw new Error('쿠폰 코드를 입력해주세요.');
      }
      if (!formData.name.trim()) {
        throw new Error('쿠폰명을 입력해주세요.');
      }
      if (formData.discount_value <= 0) {
        throw new Error('할인 값을 입력해주세요.');
      }
      if (new Date(formData.valid_from) >= new Date(formData.valid_until)) {
        throw new Error('만료일은 시작일보다 늦어야 합니다.');
      }

      // API 호출 데이터 준비
      const submitData = {
        ...formData,
        min_order_amount: formData.min_order_amount || undefined,
        max_discount_amount: formData.max_discount_amount || undefined,
        usage_limit: formData.usage_limit || undefined,
        valid_from: `${formData.valid_from}T00:00:00`,
        valid_until: `${formData.valid_until}T23:59:59`
      };

      if (isEdit && couponId) {
        // 수정
        await updateCoupon(couponId, submitData);
        alert('쿠폰이 성공적으로 수정되었습니다.');
      } else {
        // 생성
        await createCoupon(submitData);
        alert('쿠폰이 성공적으로 생성되었습니다.');
      }
      
      navigate('/admin/coupon');
    } catch (err) {
      console.error('쿠폰 저장 실패:', err);
      setError(err instanceof Error ? err.message : '쿠폰 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 권한 확인
  if (!hasAdminPermission() && !hasSystemPermission()) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h2>접근 권한이 없습니다</h2>
          <p>이 페이지에 접근하려면 관리자 권한이 필요합니다.</p>
        </div>
      </div>
    );
  }

  if (loading && isEdit) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <p>쿠폰 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button 
            className={styles.btnSecondary}
            onClick={() => navigate('/admin/coupon')}
          >
            ← 목록으로
          </button>
          <h1 className="admin-page-title">{isEdit ? '쿠폰 수정' : '쿠폰 생성'}</h1>
        </div>
      </div>

      <div className={styles.content}>
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* 에러 메시지 */}
          {error && (
            <div className={styles.errorContainer}>
              <p>{error}</p>
            </div>
          )}

          {/* 기본 정보 */}
          <div className={styles.section}>
            <h2>기본 정보</h2>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="code">쿠폰 코드 *</label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="예: WELCOME10"
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="name">쿠폰명 *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="예: 신규 가입 할인 쿠폰"
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="coupon_type">쿠폰 타입</label>
                <select
                  id="coupon_type"
                  name="coupon_type"
                  value={formData.coupon_type}
                  onChange={handleInputChange}
                  className={styles.select}
                >
                  <option value="discount">할인</option>
                  <option value="membership">멤버십</option>
                  <option value="gift">선물</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="status">상태</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className={styles.select}
                >
                  <option value="active">활성</option>
                  <option value="inactive">비활성</option>
                </select>
              </div>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="description">설명</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="쿠폰에 대한 설명을 입력하세요"
                className={styles.textarea}
                rows={3}
              />
            </div>
          </div>

          {/* 할인 정보 */}
          <div className={styles.section}>
            <h2>할인 정보</h2>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="discount_type">할인 타입</label>
                <select
                  id="discount_type"
                  name="discount_type"
                  value={formData.discount_type}
                  onChange={handleInputChange}
                  className={styles.select}
                >
                  <option value="percentage">퍼센트 할인</option>
                  <option value="fixed">고정 금액 할인</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="discount_value">할인 값 *</label>
                <input
                  type="number"
                  id="discount_value"
                  name="discount_value"
                  value={formData.discount_value}
                  onChange={handleInputChange}
                  placeholder={formData.discount_type === 'percentage' ? '10' : '5000'}
                  className={styles.input}
                  min="0"
                  step={formData.discount_type === 'percentage' ? '1' : '100'}
                  required
                />
                <small className={styles.inputHint}>
                  {formData.discount_type === 'percentage' ? '%' : '원'}
                </small>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="min_order_amount">최소 주문 금액</label>
                <input
                  type="number"
                  id="min_order_amount"
                  name="min_order_amount"
                  value={formData.min_order_amount}
                  onChange={handleInputChange}
                  placeholder="0"
                  className={styles.input}
                  min="0"
                  step="1000"
                />
                <small className={styles.inputHint}>원 (0이면 제한 없음)</small>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="max_discount_amount">최대 할인 금액</label>
                <input
                  type="number"
                  id="max_discount_amount"
                  name="max_discount_amount"
                  value={formData.max_discount_amount}
                  onChange={handleInputChange}
                  placeholder="0"
                  className={styles.input}
                  min="0"
                  step="1000"
                />
                <small className={styles.inputHint}>원 (0이면 제한 없음)</small>
              </div>
            </div>
          </div>

          {/* 사용 제한 */}
          <div className={styles.section}>
            <h2>사용 제한</h2>
            <div className={styles.formGroup}>
              <label htmlFor="usage_limit">사용 횟수 제한</label>
              <input
                type="number"
                id="usage_limit"
                name="usage_limit"
                value={formData.usage_limit}
                onChange={handleInputChange}
                placeholder="0"
                className={styles.input}
                min="0"
                step="1"
              />
              <small className={styles.inputHint}>회 (0이면 제한 없음)</small>
            </div>
          </div>

          {/* 유효 기간 */}
          <div className={styles.section}>
            <h2>유효 기간</h2>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="valid_from">시작일 *</label>
                <input
                  type="date"
                  id="valid_from"
                  name="valid_from"
                  value={formData.valid_from}
                  onChange={handleInputChange}
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="valid_until">만료일 *</label>
                <input
                  type="date"
                  id="valid_until"
                  name="valid_until"
                  value={formData.valid_until}
                  onChange={handleInputChange}
                  className={styles.input}
                  required
                />
              </div>
            </div>
          </div>

          {/* 버튼 */}
          <div className={styles.buttonContainer}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => navigate('/admin/coupon')}
            >
              취소
            </button>
            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={loading}
            >
              {loading ? '저장 중...' : (isEdit ? '수정' : '생성')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CouponEdit;
